import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from '@saleshandy/icons';
import Transfer from '../../../../shared/design-system/components/transfer/transfer';
import { Placement } from '../../../../shared/design-system/components/overlay-taget/enums';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { SortOrder } from '../../../../shared/enums/order';
import { OverlayTooltip } from '../../../../shared/design-system/components/overlay';
import { SortByOptionsProps } from './types';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';

const SortByOptions: React.FC<SortByOptionsProps> = ({
  options,
  currentSortBy,
  fieldSortOrders = {},
  onSort,
  setProspectViewIdFilter,
}) => {
  const currentOption = useMemo(() => {
    if (!currentSortBy) return options[0];
    const currentSortByString = String(currentSortBy);

    let foundOption = options.find(
      (opt) => String(opt.key) === currentSortByString,
    );

    if (!foundOption && /^\d+$/.test(currentSortByString)) {
      foundOption = options.find(
        (opt) =>
          /^\d+$/.test(String(opt.key)) &&
          Number(opt.key) === Number(currentSortByString),
      );
    }

    return foundOption || options[0];
  }, [currentSortBy, options]);

  const [selectedOption, setSelectedOption] = useState(currentOption);

  useEffect(() => {
    setSelectedOption(currentOption);
  }, [currentOption]);

  const getFieldSortOrder = (fieldKey: string): SortOrder => {
    if (fieldSortOrders[fieldKey]) {
      return fieldSortOrders[fieldKey];
    }
    const stringKey = String(fieldKey);
    if (fieldSortOrders[stringKey]) {
      return fieldSortOrders[stringKey];
    }
    return SortOrder.ASC;
  };

  const handleSortOrderChange = (
    e: React.MouseEvent | React.KeyboardEvent,
    optionKey: string,
  ) => {
    e.stopPropagation();
    // Reset the locally set Redux filter state for prospect viewId filters
    setProspectViewIdFilter?.(null);
    // Find the option and call onSort with it to update the field's sort order
    // Pass true to indicate this is a toggle action
    const option = options.find((opt) => opt.key === optionKey);
    if (option) {
      onSort(option, true);
    }
  };

  const handleLabelClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    optionKey: string,
  ) => {
    e.stopPropagation();
    // Reset the locally set Redux filter state for prospect viewId filters
    setProspectViewIdFilter?.(null);
    const option = options.find((opt) => opt.key === optionKey);
    if (option && option.key !== 'none') {
      const fieldKey = String(option.key);
      const fieldSortOrder = getFieldSortOrder(fieldKey);
      const orderToPass = fieldSortOrder;
      onSort(option, false, orderToPass);
    } else if (option) {
      setSelectedOption(option);
    }
  };

  return (
    <Transfer
      options={options}
      selectedOptionKey={selectedOption?.key}
      optionRenderer={(option) => {
        const fieldKey = String(option.key);
        const fieldSortOrder = getFieldSortOrder(fieldKey);

        return (
          <div
            className="sort-by-option-row d-flex justify-content-between w-100"
            {...accessibleOnClick((e) => handleLabelClick(e, option.key))}
          >
            <div className="sort-by-option-label d-flex align-items-center">
              <span className="sort-by-option" key={option.key}>
                <TruncatedTextWithAutoTooltip text={option?.name} />
              </span>
              {option.key === 'none' && (
                <span
                  className="sort-by-option-default"
                  key={option.key}
                  {...accessibleOnClick(() => {
                    setSelectedOption(option);
                  })}
                >
                  (default)
                </span>
              )}
            </div>

            {option.key !== 'none' && (
              <OverlayTooltip
                text={`Click to sort in ${
                  fieldSortOrder === SortOrder.ASC ? 'Descending' : 'Ascending'
                } order`}
              >
                <div
                  className="sort-by-type"
                  {...accessibleOnClick((e) => {
                    handleSortOrderChange(e, option.key);
                    e.stopPropagation();
                  })}
                >
                  <div className="sort-by-type-icon">
                    <div
                      className={`sort-by-type-icon-up ${
                        fieldSortOrder !== SortOrder.ASC ? 'disabled' : ''
                      }`}
                    >
                      <ChevronUp />
                    </div>

                    <div
                      className={`sort-by-type-icon-down ${
                        fieldSortOrder !== SortOrder.DESC ? 'disabled' : ''
                      }`}
                    >
                      <ChevronDown />
                    </div>
                  </div>

                  <div className="sort-by-type-text">
                    {fieldSortOrder === SortOrder.ASC
                      ? 'Ascending'
                      : 'Descending'}
                  </div>
                </div>
              </OverlayTooltip>
            )}
          </div>
        );
      }}
      showSearch
      searchPlaceholder="Search"
      className="sort-by-options"
      optionFilterProp="name"
      filterOption={(value, option) =>
        option?.name?.toLowerCase().includes(value.toLowerCase())
      }
      placement={Placement.BottomEnd}
    />
  );
};

export default SortByOptions;
